import type { App } from "@open-booking/server";
import type { InviteMemberPayload, UpdateMemberPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type ListMembersResponse = App["~Routes"]["properties"][":id"]["members"]["get"]["response"]["200"];
type InviteMemberResponse = App["~Routes"]["properties"][":id"]["members"]["invite"]["post"]["response"]["200"];
type UpdateMemberResponse = App["~Routes"]["properties"][":id"]["members"][":memberId"]["patch"]["response"]["200"];
type RemoveMemberResponse = App["~Routes"]["properties"][":id"]["members"][":memberId"]["delete"]["response"]["200"];
type TeamOverviewResponse = App["~Routes"]["me"]["team"]["get"]["response"]["200"];
type RevokeInvitationResponse = App["~Routes"]["me"]["team"]["invitations"][":invitationId"]["delete"]["response"]["200"];
type PublicInvitationResponse = App["~Routes"]["public"]["invitations"][":token"]["get"]["response"]["200"];
type TeamContextResponse = App["~Routes"]["me"]["team-context"]["get"]["response"]["200"];

export const teamApi = createApi({
	reducerPath: "teamApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Team", "PropertyMembers", "TeamContext"],
	endpoints: (builder) => ({
		getTeamOverview: builder.query<TeamOverviewResponse, void>({
			query: (): EdenCall<TeamOverviewResponse> => (client) => client.me.team.get(),
			providesTags: ["Team"],
		}),
		getMyTeamContext: builder.query<TeamContextResponse, void>({
			query: (): EdenCall<TeamContextResponse> => (client) => client.me["team-context"].get(),
			providesTags: ["TeamContext"],
		}),
		listPropertyMembers: builder.query<ListMembersResponse, string>({
			query:
				(id): EdenCall<ListMembersResponse> =>
				(client) =>
					client.properties({ id }).members.get(),
			providesTags: ["PropertyMembers"],
		}),
		inviteMember: builder.mutation<InviteMemberResponse, { id: string; data: InviteMemberPayload }>({
			query:
				({ id, data }): EdenCall<InviteMemberResponse> =>
				(client) =>
					client.properties({ id }).members.invite.post(data),
			invalidatesTags: ["Team", "PropertyMembers"],
		}),
		updateMember: builder.mutation<UpdateMemberResponse, { id: string; memberId: string; data: UpdateMemberPayload }>({
			query:
				({ id, memberId, data }): EdenCall<UpdateMemberResponse> =>
				(client) =>
					client.properties({ id }).members({ memberId }).patch(data),
			invalidatesTags: ["Team", "PropertyMembers"],
		}),
		removeMember: builder.mutation<RemoveMemberResponse, { id: string; memberId: string }>({
			query:
				({ id, memberId }): EdenCall<RemoveMemberResponse> =>
				(client) =>
					client.properties({ id }).members({ memberId }).delete(),
			invalidatesTags: ["Team", "PropertyMembers"],
		}),
		revokeInvitation: builder.mutation<RevokeInvitationResponse, string>({
			query:
				(invitationId): EdenCall<RevokeInvitationResponse> =>
				(client) =>
					client.me.team.invitations({ invitationId }).delete(),
			invalidatesTags: ["Team"],
		}),
		getPublicInvitation: builder.query<PublicInvitationResponse, string>({
			query:
				(token): EdenCall<PublicInvitationResponse> =>
				(client) =>
					client.public.invitations({ token }).get(),
		}),
	}),
});

export const {
	useGetTeamOverviewQuery,
	useGetMyTeamContextQuery,
	useListPropertyMembersQuery,
	useInviteMemberMutation,
	useUpdateMemberMutation,
	useRemoveMemberMutation,
	useRevokeInvitationMutation,
	useGetPublicInvitationQuery,
} = teamApi;
